import type { SxProps, Theme } from "@mui/material";

export const isHtmlContent = (text: string): boolean => {
	return /<[a-z][\s\S]*>/i.test(text);
};

const isNearBlack = (r: number, g: number, b: number): boolean => {
	return r < 60 && g < 60 && b < 60;
};

const isNearWhite = (r: number, g: number, b: number): boolean => {
	return r > 200 && g > 200 && b > 200;
};

export const sanitizeHtmlColors = (
	html: string,
	isDarkMode: boolean,
): string => {
	if (!isDarkMode) {
		return html;
	}

	let sanitized = html.replace(
		/color:\s*rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/gi,
		(match, r, g, b) => {
			const red = Number.parseInt(r, 10);
			const green = Number.parseInt(g, 10);
			const blue = Number.parseInt(b, 10);

			if (isNearBlack(red, green, blue) || isNearWhite(red, green, blue)) {
				return "color: inherit";
			}
			return match;
		},
	);

	sanitized = sanitized.replace(
		/color:\s*(black|#000000?)/gi,
		"color: inherit",
	);

	sanitized = sanitized.replace(
		/color:\s*(white|#fff(?:fff)?)/gi,
		"color: inherit",
	);

	return sanitized;
};

type HtmlContentSxOptions = {
	theme: Theme;
	showMore: boolean;
	maxLines: number;
};

export const getHtmlContentSx = ({
	theme,
	showMore,
	maxLines,
}: HtmlContentSxOptions): SxProps<Theme> => ({
	fontSize: 14,
	fontWeight: 400,
	color: theme.palette.grey[700],
	overflowWrap: "break-word",
	maxWidth: "100%",
	overflow: "hidden",
	...(showMore && {
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: maxLines,
	}),
	"& p": {
		margin: 0,
		marginBottom: "0.5em",
		"&:last-child": { marginBottom: 0 },
	},
	"& h1, & h2, & h3, & h4, & h5, & h6": {
		margin: "0.5em 0",
		fontWeight: 600,
	},
	"& h1": { fontSize: "1.5em" },
	"& h2": { fontSize: "1.25em" },
	"& h3": { fontSize: "1.1em" },
	"& ul, & ol": {
		margin: "0.5em 0",
		paddingLeft: "1.5em",
	},
	"& li": {
		marginBottom: "0.25em",
	},
	"& pre": {
		backgroundColor: theme.palette.grey[100],
		padding: "0.75em",
		borderRadius: "4px",
		overflow: "auto",
		margin: "0.5em 0",
	},
	"& code": {
		fontFamily: "monospace",
		fontSize: "0.9em",
		backgroundColor: theme.palette.grey[100],
		padding: "0.1em 0.3em",
		borderRadius: "3px",
	},
	"& pre code": {
		backgroundColor: "transparent",
		padding: 0,
	},
	"& hr": {
		border: "none",
		borderTop: `1px solid ${theme.palette.grey[300]}`,
		margin: "0.75em 0",
	},
	"& strong": { fontWeight: 600 },
	"& em": { fontStyle: "italic" },
	"& s": { textDecoration: "line-through" },
	"& a": {
		color: theme.palette.primary.main,
		textDecoration: "underline",
	},
});
